import React, { useEffect, useMemo, useState } from "react";
import { Loader2, Search, KeyRound, RefreshCw, Trash2, Save, CheckCircle2, AlertCircle, ShieldCheck } from "lucide-react";
import { adminApi } from "../../lib/api";
import { Input } from "../ui/input";
import { Button } from "../ui/button";
import { Switch } from "../ui/switch";
import { useToast } from "../../hooks/use-toast";

const PERM_LABELS = [
  { key: "admin_access", label: "Admin-Panel Zugriff", desc: "Darf das Admin-Dashboard oeffnen" },
  { key: "news_write", label: "News schreiben", desc: "Darf eigene News-Artikel erstellen & bearbeiten" },
  { key: "news_review", label: "News freigeben", desc: "Darf eingereichte Artikel pruefen & veroeffentlichen" },
  { key: "news_sticky", label: "News pinnen", desc: "Darf Artikel als 'Sticky' markieren (oben anheften)" },
  { key: "comments_moderate", label: "Kommentare moderieren", desc: "Darf Kommentare verbergen oder loeschen, wenn sie gegen die Richtlinien verstoßen" },
  { key: "events_edit", label: "Events verwalten (Eventteam)", desc: "Darf Habbo-Events erstellen, bearbeiten & Badges hinterlegen" },
  { key: "projects_edit", label: "Projekte verwalten (Content)", desc: "Content-Team: Darf Habbo-Projekte pflegen & Mitglieder hinzufuegen" },
  { key: "badges_edit", label: "Badges verwalten (Content)", desc: "Content-Team: Darf Badges anlegen & pflegen" },
  { key: "rares_edit", label: "Rares verwalten (Content)", desc: "Content-Team: Darf Rares anlegen, Usern zuweisen & pflegen" },
  { key: "furniture_edit", label: "Furniture verwalten (Content)", desc: "Content-Team: Darf Furniture-Eintraege anlegen & pflegen" },
  { key: "media_access", label: "Medien-Bibliothek", desc: "Darf Medien-Bibliothek, Promo-Designer, Habbo-Bilder & Neue Moebel nutzen (Upload, Crawl, URLs)" },
  { key: "ai_import", label: "KI-News-Import", desc: "Darf offizielle Habbo-News per KI umschreiben & als Draft importieren" },
  { key: "jobs_manage", label: "Jobs & Bewerbungen", desc: "Darf Job-Ausschreibungen verwalten und Bewerbungen einsehen/bearbeiten" },
  { key: "support_manage", label: "Support-Anfragen", desc: "Darf Support-Anfragen der User beantworten & verwalten" },
];

const UsersPanel = ({ token }) => {
  const a = useMemo(() => adminApi(token), [token]);
  const { toast } = useToast();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [expanded, setExpanded] = useState(null);
  const [resetPwd, setResetPwd] = useState({});
  const [savingId, setSavingId] = useState(null);
  const [savingPerm, setSavingPerm] = useState(null);

  const load = async () => {
    setLoading(true);
    try { setUsers(await a.listUsers()); } finally { setLoading(false); }
  };
  useEffect(() => { load();  }, []);

  const filtered = users.filter((u) => {
    if (!q.trim()) return true;
    const s = q.toLowerCase();
    return u.habbo_name.toLowerCase().includes(s);
  });

  const update = (id, field, val) =>
    setUsers((prev) => prev.map((x) => (x.id === id ? { ...x, [field]: val } : x)));

  const onSave = async (u) => {
    setSavingId(u.id);
    try {
      await a.updateUser(u.id, { habbo_name: u.habbo_name, verified: u.verified });
      toast({ title: "Gespeichert" });
      await load();
    } catch (err) {
      toast({ title: "Fehler", description: err?.response?.data?.detail, variant: "destructive" });
    } finally { setSavingId(null); }
  };

  const onResetPassword = async (u) => {
    const pw = resetPwd[u.id];
    if (!pw || pw.length < 6) {
      toast({ title: "Passwort zu kurz", description: "Mind. 6 Zeichen", variant: "destructive" });
      return;
    }
    if (!window.confirm(`Passwort fuer ${u.habbo_name} wirklich zuruecksetzen?`)) return;
    try {
      await a.resetUserPassword(u.id, pw);
      setResetPwd((prev) => ({ ...prev, [u.id]: "" }));
      toast({ title: "Passwort zurueckgesetzt" });
    } catch (err) {
      toast({ title: "Fehler", variant: "destructive" });
    }
  };

  const onRegenCode = async (u) => {
    if (!window.confirm(`Neuen Verifizierungscode fuer ${u.habbo_name} erzeugen?`)) return;
    try {
      await a.regenerateUserCode(u.id);
      toast({ title: "Neuer Code erzeugt" });
      await load();
    } catch { toast({ title: "Fehler", variant: "destructive" }); }
  };

  const onDelete = async (u) => {
    if (!window.confirm(`User "${u.habbo_name}" wirklich loeschen?`)) return;
    try { await a.deleteUser(u.id); toast({ title: "Geloescht" }); await load(); }
    catch { toast({ title: "Fehler", variant: "destructive" }); }
  };

  const onMakeAdmin = async (u) => {
    const isAdmin = !!u.permissions?.admin_access;
    if (!window.confirm(isAdmin
      ? `Admin-Rechte von "${u.habbo_name}" entfernen?`
      : `${u.habbo_name} zum Admin machen?\nDamit bekommt diese Person ALLE Berechtigungen.`)) return;
    setSavingPerm(`${u.id}-makeadmin`);
    try {
      const adminPerms = isAdmin
        ? {} // alle weg
        : {
            admin_access: true, news_write: true, news_review: true, news_sticky: true,
            events_edit: true, projects_edit: true, badges_edit: true, rares_edit: true,
            furniture_edit: true, comments_moderate: true, ai_import: true,
          };
      await a.updatePermissions(u.id, adminPerms);
      setUsers((prev) => prev.map((x) => (x.id === u.id ? { ...x, permissions: adminPerms } : x)));
      toast({ title: isAdmin ? "Admin-Rechte entfernt" : "Zum Admin gemacht" });
    } catch (err) {
      toast({ title: "Fehler", description: err?.response?.data?.detail, variant: "destructive" });
    } finally {
      setSavingPerm(null);
    }
  };

  const onTogglePerm = async (u, key, value) => {
    setSavingPerm(`${u.id}-${key}`);
    try {
      const newPerms = { ...(u.permissions || {}), [key]: value };
      await a.updatePermissions(u.id, newPerms);
      setUsers((prev) => prev.map((x) => (x.id === u.id ? { ...x, permissions: newPerms } : x)));
      toast({ title: "Berechtigung gespeichert" });
    } catch (err) {
      toast({ title: "Fehler", description: err?.response?.data?.detail, variant: "destructive" });
    } finally {
      setSavingPerm(null);
    }
  };

  return (
    <div>
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div className="relative w-full max-w-sm">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-zinc-500" />
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="User suchen ..." className="pl-9 bg-[#0f141b] border-white/10 text-zinc-200" />
        </div>
        <div className="text-xs text-zinc-500">{filtered.length} / {users.length} User</div>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-20 text-zinc-400"><Loader2 className="h-5 w-5 animate-spin" /></div>
      ) : (
        <div className="mt-4 space-y-2">
          {filtered.map((u) => {
            const open = expanded === u.id;
            return (
              <div key={u.id} className="rounded-xl border border-white/10 bg-[#0f141b]">
                <button onClick={() => setExpanded(open ? null : u.id)} className="flex w-full items-center justify-between gap-3 px-4 py-3 text-left">
                  <div className="flex items-center gap-3">
                    <img src={u.avatar} alt={u.habbo_name} className="hb-pixel-img h-10" />
                    <div>
                      <div className="text-sm font-semibold text-white flex items-center gap-2">
                        {u.habbo_name}
                        {u.verified ? (
                          <CheckCircle2 className="h-4 w-4 text-emerald-400" />
                        ) : (
                          <AlertCircle className="h-4 w-4 text-[#ffb84d]" />
                        )}
                      </div>
                      <div className="text-xs text-zinc-500">{u.points || 0} Punkte</div>
                    </div>
                  </div>
                  <span className="text-xs text-zinc-500">{open ? "Schliessen" : "Bearbeiten"}</span>
                </button>
                {open && (
                  <div className="border-t border-white/10 p-4 space-y-4">
                    {/* Stammdaten */}
                    <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
                      <div>
                        <label className="text-xs uppercase tracking-wider text-zinc-500">Habbo-Name</label>
                        <Input value={u.habbo_name} onChange={(e) => update(u.id, "habbo_name", e.target.value)} className="mt-1 bg-[#10141b] border-white/10 text-zinc-200" />
                      </div>
                      <div className="md:col-span-2 flex items-center gap-3 rounded-lg bg-[#10141b] p-3 border border-white/10">
                        <Switch checked={!!u.verified} onCheckedChange={(v) => update(u.id, "verified", v)} />
                        <div>
                          <div className="text-sm font-semibold text-white">Habbo verifiziert</div>
                          <div className="text-xs text-zinc-500">Manuell freischalten ohne Motto-Pruefung</div>
                        </div>
                      </div>
                      <div className="md:col-span-2 text-xs text-zinc-500">
                        Code: <span className="text-[#ffb84d]">{u.verification_code}</span> · Erstellt: {String(u.created_at || "").slice(0, 10)}
                      </div>
                    </div>
                    <div className="flex flex-wrap items-center gap-2">
                      <Button onClick={() => onSave(u)} disabled={savingId === u.id} className="btn-primary rounded-lg px-3 py-1.5 text-xs">
                        {savingId === u.id ? <Loader2 className="mr-1 h-4 w-4 animate-spin" /> : <Save className="mr-1 h-4 w-4" />}
                        Speichern
                      </Button>
                      <Button onClick={() => onRegenCode(u)} className="btn-ghost rounded-lg px-3 py-1.5 text-xs">
                        <RefreshCw className="mr-1 h-4 w-4" /> Neuer Code
                      </Button>
                      <Button onClick={() => onDelete(u)} className="btn-ghost rounded-lg px-3 py-1.5 text-xs text-rose-300 hover:border-rose-500/40">
                        <Trash2 className="mr-1 h-4 w-4" /> Loeschen
                      </Button>
                    </div>

                    {/* Passwort-Reset */}
                    <div className="rounded-lg border border-white/10 bg-[#10141b] p-3">
                      <div className="text-sm font-semibold text-white flex items-center gap-2">
                        <KeyRound className="h-4 w-4 text-[#b39bff]" /> Passwort zuruecksetzen
                      </div>
                      <div className="mt-2 flex flex-col gap-2 sm:flex-row">
                        <Input
                          type="password"
                          value={resetPwd[u.id] || ""}
                          onChange={(e) => setResetPwd((prev) => ({ ...prev, [u.id]: e.target.value }))}
                          placeholder="Neues Passwort (min. 6 Zeichen)"
                          className="flex-1 bg-[#0f141b] border-white/10 text-zinc-200"
                        />
                        <Button onClick={() => onResetPassword(u)} disabled={!(resetPwd[u.id] && resetPwd[u.id].length >= 6)} className="btn-primary rounded-lg px-3 py-2 text-sm">
                          Setzen
                        </Button>
                      </div>
                    </div>

                    {/* Berechtigungen */}
                    <div className="rounded-lg border border-white/10 bg-[#10141b] p-3" data-testid={`perms-${u.id}`}>
                      <div className="mb-3 flex items-center justify-between">
                        <div className="text-sm font-semibold text-white flex items-center gap-2">
                          <ShieldCheck className="h-4 w-4 text-[#b39bff]" /> Berechtigungen
                        </div>
                        <Button
                          onClick={() => onMakeAdmin(u)}
                          disabled={savingPerm === `${u.id}-makeadmin`}
                          data-testid={`make-admin-${u.id}`}
                          className={`rounded-lg px-3 py-1.5 text-xs font-semibold ${u.permissions?.admin_access ? "border border-rose-500/40 bg-rose-500/10 text-rose-200 hover:bg-rose-500/20" : "btn-primary"}`}
                        >
                          {savingPerm === `${u.id}-makeadmin` ? <Loader2 className="mr-1 h-3.5 w-3.5 animate-spin" /> : <ShieldCheck className="mr-1 h-3.5 w-3.5" />}
                          {u.permissions?.admin_access ? "Admin-Rechte entziehen" : "Zum Admin machen"}
                        </Button>
                      </div>
                      <div className="text-sm font-semibold text-white flex items-center gap-2">
                        <ShieldCheck className="h-4 w-4 text-[#7c4dff]" /> Berechtigungen
                      </div>
                      <div className="mt-3 space-y-2">
                        {PERM_LABELS.map((p) => {
                          const checked = !!(u.permissions && u.permissions[p.key]);
                          const saving = savingPerm === `${u.id}-${p.key}`;
                          return (
                            <div key={p.key} className="flex items-center justify-between gap-3 rounded-md border border-white/5 bg-[#0f141b] px-3 py-2">
                              <div>
                                <div className="text-sm font-semibold text-white">{p.label}</div>
                                <div className="text-xs text-zinc-500">{p.desc}</div>
                              </div>
                              <div className="flex items-center gap-2">
                                {saving && <Loader2 className="h-3.5 w-3.5 animate-spin text-zinc-400" />}
                                <Switch
                                  checked={checked}
                                  disabled={!u.verified}
                                  onCheckedChange={(v) => onTogglePerm(u, p.key, v)}
                                  data-testid={`perm-${p.key}-${u.id}`}
                                />
                              </div>
                            </div>
                          );
                        })}
                        {!u.verified && (
                          <div className="text-xs text-[#ffb84d]">User muss erst verifiziert sein, bevor Rechte vergeben werden koennen.</div>
                        )}
                      </div>
                    </div>

                    {/* Punkte vergeben */}
                    <div>
                      <div className="text-sm font-semibold text-white flex items-center gap-2">
                        🎁 Punkte vergeben
                      </div>
                      <div className="mt-2 text-xs text-zinc-500">Aktueller Stand: <span className="font-bold text-amber-300">{u.points ?? 0}</span> Punkte</div>
                      <PointsAwardForm
                        userId={u.id}
                        userName={u.habbo_name}
                        onAwarded={(newPoints) => {
                          setUsers((prev) => prev.map((x) => x.id === u.id ? { ...x, points: newPoints } : x));
                        }}
                        a={a}
                        toast={toast}
                      />
                    </div>
                  </div>
                )}
              </div>
            );
          })}
          {filtered.length === 0 && (
            <div className="py-10 text-center text-sm text-zinc-500">Noch keine registrierten User.</div>
          )}
        </div>
      )}
    </div>
  );
};

const PointsAwardForm = ({ userId, userName, onAwarded, a, toast }) => {
  const [amount, setAmount] = useState(50);
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  const submit = async () => {
    if (!reason.trim()) {
      toast({ title: "Grund fehlt", description: "Bitte einen Grund eingeben.", variant: "destructive" });
      return;
    }
    setBusy(true);
    try {
      const r = await a.awardPoints(userId, amount, reason.trim());
      toast({ title: "Punkte vergeben", description: `${userName} hat jetzt ${r.points} Punkte (${r.tier?.label}).` });
      onAwarded?.(r.points);
      setReason("");
    } catch (err) {
      toast({ title: "Fehler", description: err?.response?.data?.detail || "Vergabe fehlgeschlagen", variant: "destructive" });
    } finally { setBusy(false); }
  };
  return (
    <div className="mt-2 flex flex-wrap items-center gap-2 rounded-md border border-white/5 bg-[#0f141b] p-2">
      <Input
        type="number"
        value={amount}
        onChange={(e) => setAmount(parseInt(e.target.value, 10) || 0)}
        className="h-8 w-20 bg-[#10141b] border-white/10 text-zinc-200 text-sm"
        data-testid={`points-amount-${userId}`}
      />
      <Input
        value={reason}
        onChange={(e) => setReason(e.target.value)}
        placeholder="Grund (z.B. Gewinnspiel #5)"
        className="h-8 flex-1 min-w-[180px] bg-[#10141b] border-white/10 text-zinc-200 text-sm"
        data-testid={`points-reason-${userId}`}
      />
      <Button
        type="button"
        onClick={submit}
        disabled={busy || !reason.trim()}
        className="btn-primary h-8 rounded-md px-3 text-xs"
        data-testid={`points-award-${userId}`}
      >
        {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : "Vergeben"}
      </Button>
    </div>
  );
};

export default UsersPanel;
